import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { prizeForRun } from '../../domain/game/carCollection';

/**
 * The car a run has won, held until the reader has been shown it
 * (SPEC-010 PR-10.4, PR-10.11).
 *
 * The other half of `useCarCollection`, and it faces the other way: that hook
 * produces the pool a run is dealt from, this one consumes the run that pool
 * produced. Written as two hooks rather than one because one would be a cycle —
 * the pool cannot depend on the run that is dealt from it.
 *
 * TWO MOMENTS A CAR ARRIVES IN, and they are the same ceremony:
 *
 *   mid-run  the score passed the rung on a round the reader got RIGHT. The
 *            ceremony opens ITSELF, over a run that is still going: the car is
 *            banked, the bar moves to the score that bought it, and the rung
 *            above is live before the next question is dealt. The run carries on
 *            underneath with its streak intact, which is how one run wins
 *            several.
 *   an ending  the run stopped on a rung it had cleared — because the score
 *            crossed it on the very answer that ended the run, or on a round the
 *            clock ran out on.
 *
 * NEITHER IS ASKED FOR (SPEC-010 D-35). The mid-run car was offered as a button
 * for a revision and taking it was a tap the reader had already earned: a prize
 * that has to be requested is a prize the product is unsure about. Both doors
 * now open on their own, and the only control the reader has is the way out.
 *
 * The award and the CEREMONY are deliberately separate moments in both. The car
 * is put in the garage first, before anything is drawn, because a prize that
 * only landed once an animation had been watched is a prize a backgrounded tab
 * or a dismissed dialog can lose. What the reader sees is the second thing, and
 * `dismiss` is what ends it — it closes the ceremony and does not touch the
 * collection, because by then there is nothing left to undo.
 *
 * Shown ONCE. There is no way back into a ceremony and that is the design
 * (SPEC-010 D-13): its whole value is that it happened, and the durable version
 * of it is the car's own detail screen, which is a tap away for ever afterwards.
 */
export default function useCarPrize({
    run,
    cars,
    wonIds,
    onAward,
    onClaim,
}) {
    /**
     * ONE piece of state for two questions, keyed on the VERDICT it was made
     * against.
     *
     * `prize` asks "is the ceremony open"; `awarded` asks "did this run end
     * having won something", and the screen needs the second long after the
     * first has been dismissed — the gap under the ladder would otherwise tell a
     * winner they were a thousand points short, because by then the ladder has
     * moved up a rung underneath them.
     *
     * Keying on the verdict is what makes both expire on their own. A verdict is
     * a fresh object per ROUND, so it distinguishes "this run, this moment"
     * across every re-render between the ending and the restart — and across the
     * rounds a collected car is celebrated between, which is why a mid-run
     * ceremony closes itself the moment the reader deals the next question.
     */
    const [ceremony, setCeremony] = useState(null);

    // Held in refs so a parent that re-renders — which the duel screen does on
    // every tick of its clock — cannot restart either one. `useStartLights`
    // holds its `onGo` the same way and for the same reason.
    const award = useRef(onAward);
    const rebase = useRef(onClaim);

    award.current = onAward;
    rebase.current = onClaim;

    /**
     * The car this LIVE run has just bought.
     *
     * Read on the reveal and nowhere else, which is a rule about the CLOCK
     * rather than about the ladder: a question that is still open is a question
     * whose seconds are draining, and a ceremony over a running clock would end
     * the run it was celebrating from behind a modal. On the reveal the clock is
     * stopped, the reader is reading an answer, and nothing is taken from them
     * by a car arriving.
     *
     * It answers null again the instant the car is banked, without a flag to
     * clear: the collection has grown by one and the bar has moved to the score
     * that bought it, so the same question asked a render later is a question
     * about a rung nothing has paid for.
     */
    const claimable = useMemo(() => (
        run?.status === 'revealed'
            ? prizeForRun({
                cars, wonIds, record: run.record, score: run.score,
            })
            : null
    ), [run, cars, wonIds]);

    /**
     * The verdict the last award was made against, in a ref rather than read
     * back out of `ceremony`.
     *
     * It has to be a ref because the WRITE cannot live inside a state updater:
     * React may call an updater more than once, and `useDuelRun` banks its
     * finished run under the same rule. The guard therefore has to be readable
     * outside one, which state is not.
     */
    const claimed = useRef(null);

    /**
     * ONE effect for both doors, because they are one rule read at two moments:
     * a rung this run has cleared and not yet been paid for.
     *
     * Everything in here happens OUTSIDE a state updater — React may call an
     * updater more than once, and one of these three writes reaches Web Storage.
     * They are set together, so React batches them into a single render: there
     * is no frame in which the collection has grown against a bar that has not
     * moved, which would be a frame quoting the next car at a rung's margin
     * minus this one.
     *
     * The MID-RUN door additionally re-bases the run's bar. The ending's does
     * not, and does not need to: the run is over, and `useDuelRun` re-reads the
     * record from the board when the next one opens.
     */
    useEffect(() => {
        if (!run || claimed.current === run.verdict) {
            return;
        }

        if (run.status === 'revealed') {
            if (!claimable) {
                return;
            }

            claimed.current = run.verdict;
            award.current(claimable.id);
            rebase.current?.();
            setCeremony({ verdict: run.verdict, car: claimable, isSeen: false });

            return;
        }

        if (run.status !== 'over') {
            return;
        }

        claimed.current = run.verdict;

        const won = prizeForRun({
            cars,
            wonIds,
            record: run.record,
            score: run.score,
        });

        if (won) {
            award.current(won.id);
        }

        // A run that won nothing still records a ceremony, and it records it as
        // already SEEN: that is what stops the reveal opening while leaving
        // `awarded` able to answer honestly for the run on screen.
        setCeremony({ verdict: run.verdict, car: won, isSeen: !won });
    }, [run, cars, wonIds, claimable]);

    const dismiss = useCallback(
        () => setCeremony((current) => (current ? { ...current, isSeen: true } : current)),
        [],
    );

    const isThisMoment = Boolean(ceremony) && run?.verdict === ceremony.verdict;

    return {
        // The ceremony, while it is open — from either door.
        prize: isThisMoment && !ceremony.isSeen ? ceremony.car : null,
        // What the ENDED run won, for as long as that run is the one on screen —
        // dismissing the ceremony does not un-win a car. Only ever the run's own
        // ending: a car collected mid-run leaves a ladder that is honest again a
        // rung higher, and a band that went on saying "secured" would be hiding
        // the next car rather than protecting the last one.
        awarded: isThisMoment && run?.status === 'over' ? ceremony.car : null,
        dismiss,
    };
}
